import React, { useState, useEffect, useRef } from 'react';
import { Play, ExternalLink, Mic, Video, FileText, Calendar, Plus, Trash2 } from 'lucide-react';

const MediaPage = ({ content, language, isEditing, updateContent, addToArray, removeFromArray }) => {
  const [mediaVisible, setMediaVisible] = useState(false);
  const [podcastVisible, setPodcastVisible] = useState(false);
  const mediaRef = useRef(null);
  const podcastRef = useRef(null);

  useEffect(() => {
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      if (mediaRef.current && isInViewport(mediaRef.current)) {
        setMediaVisible(true);
      }
      if (podcastRef.current && isInViewport(podcastRef.current)) {
        setPodcastVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper function to create editable text field
  const EditableText = ({ value, onChange, className, placeholder, multiline = false }) => {
    if (!isEditing) {
      return multiline ? (
        <p className={className}>{value}</p>
      ) : (
        <span className={className}>{value}</span>
      );
    }

    const Component = multiline ? 'textarea' : 'input';
    return (
      <Component
        type={multiline ? undefined : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${className} editable-field bg-transparent border-2 border-dashed border-gray-600 rounded p-2 focus:border-blue-500 focus:outline-none resize-none`}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
      />
    );
  };

  // Add new episode
  const addEpisode = () => {
    const newEpisode = {
      title: 'New Episode',
      description: 'Description of this episode...',
      date: 'Episode ' + (content[language].media.episodes.length + 1),
      link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
    };
    addToArray(`${language}.media.episodes`, newEpisode);
  };

  // Initialize additional content fields when editing starts
  useEffect(() => {
    if (isEditing && !content[language].media.subtitle) {
      updateContent(`${language}.media.subtitle`, 
        language === 'en'
          ? 'Thought leadership, insights, and conversations shaping the future of real estate development'
          : 'Liderazgo de pensamiento, insights y conversaciones que dan forma al futuro del desarrollo inmobiliario'
      );
      updateContent(`${language}.media.podcastDescription`, 
        language === 'en'
          ? 'A bilingual media platform featuring top voices in real estate, venture capital, and global innovation — offering insights that help investors and founders build generational companies and reimagine the built world.'
          : 'Una plataforma de medios bilingüe que presenta las principales voces en bienes raíces, capital de riesgo e innovación global, ofreciendo insights que ayudan a inversionistas y fundadores a construir empresas generacionales y reimaginar el mundo construido.'
      );
      updateContent(`${language}.media.newsletterTitle`, 
        language === 'en' ? 'Stay Connected' : 'Mantente Conectado'
      );
      updateContent(`${language}.media.newsletterDescription`, 
        language === 'en'
          ? 'Get insights, episode updates, and industry analysis delivered to your inbox'
          : 'Recibe insights, actualizaciones de episodios y análisis de la industria en tu bandeja de entrada'
      );
      updateContent(`${language}.media.mediaInquiriesTitle`, 
        language === 'en' ? 'Media Inquiries' : 'Consultas de Medios'
      );
      updateContent(`${language}.media.mediaInquiriesDescription`, 
        language === 'en'
          ? 'Interested in featuring Zeus or Infinity Development? We\'d love to share our story and insights.'
          : '¿Interesado en presentar a Zeus o Infinity Development? Nos encantaría compartir nuestra historia e insights.'
      );
    }
  }, [isEditing, language, content, updateContent]);

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div 
        ref={mediaRef}
        className="max-w-6xl mx-auto px-6 md:px-12"
      >
        <EditableText
          value={content[language].media.title}
          onChange={(value) => updateContent(`${language}.media.title`, value)}
          className={`text-5xl font-bold mb-8 text-center block w-full transition-all duration-700 ${mediaVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          placeholder="Page title..."
        />
        
        <div className={`h-1 w-24 bg-white mx-auto mb-8 transition-all duration-700 delay-200 ${mediaVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
        
        <EditableText
          value={content[language].media.subtitle || (language === 'en'
            ? 'Thought leadership, insights, and conversations shaping the future of real estate development'
            : 'Liderazgo de pensamiento, insights y conversaciones que dan forma al futuro del desarrollo inmobiliario')}
          onChange={(value) => updateContent(`${language}.media.subtitle`, value)}
          className={`text-xl text-gray-300 mb-16 text-center block w-full transition-all duration-700 delay-300 ${mediaVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
          placeholder="Page subtitle..."
          multiline
        />

        {/* Featured Podcast Section */}
        <div 
          ref={podcastRef}
          className={`mb-20 transition-all duration-700 delay-400 ${mediaVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
        >
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <EditableText
                    value={content[language].media.podcastTitle}
                    onChange={(value) => updateContent(`${language}.media.podcastTitle`, value)}
                    className="text-3xl font-bold"
                    placeholder="Podcast title..."
                  />
                </div>
                
                <EditableText
                  value={content[language].media.podcastDescription || (language === 'en'
                    ? 'A bilingual media platform featuring top voices in real estate, venture capital, and global innovation — offering insights that help investors and founders build generational companies and reimagine the built world.'
                    : 'Una plataforma de medios bilingüe que presenta las principales voces en bienes raíces, capital de riesgo e innovación global, ofreciendo insights que ayudan a inversionistas y fundadores a construir empresas generacionales y reimaginar el mundo construido.')}
                  onChange={(value) => updateContent(`${language}.media.podcastDescription`, value)}
                  className="text-lg text-gray-300 mb-6 block w-full"
                  placeholder="Podcast description..."
                  multiline
                />
                
                <div className="flex flex-col sm:flex-row gap-4">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={language === 'en' ? 'Listen on Spotify' : 'Escuchar en Spotify'}
                        onChange={() => {}} // This could be made editable if needed
                        className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded editable-field"
                        placeholder="Spotify button text..."
                      />
                      <input
                        type="text"
                        value={language === 'en' ? 'Be a Guest' : 'Ser Invitado'}
                        onChange={() => {}} // This could be made editable if needed
                        className="inline-flex items-center px-6 py-3 border border-white text-white font-medium rounded editable-field bg-transparent"
                        placeholder="Guest button text..."
                      />
                    </>
                  ) : (
                    <>
                      <a 
                        href="https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        {language === 'en' ? 'Listen on Spotify' : 'Escuchar en Spotify'}
                      </a>
                      <button className="inline-flex items-center px-6 py-3 border border-white text-white font-medium rounded hover:bg-white hover:text-black transition-colors">
                        <Calendar className="w-5 h-5 mr-2" />
                        {language === 'en' ? 'Be a Guest' : 'Ser Invitado'}
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Updated Podcast Cover Art Section */}
              <div className="relative rounded-lg overflow-hidden aspect-square bg-gray-800">
                <img 
                  src="./podcast.png" 
                  alt="Podcast Cover Art"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                {/* Fallback content */}
                <div className="absolute inset-0 bg-gray-800 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                  <div className="text-center">
                    <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400">Podcast Cover Art</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Episode Grid */}
        <div className={`mb-16 transition-all duration-700 delay-600 ${podcastVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <h3 className="text-3xl font-bold text-center">
              {language === 'en' ? 'Latest Episodes' : 'Episodios Recientes'}
            </h3>
            
            {isEditing && (
              <button
                onClick={addEpisode}
                className="ml-4 p-2 bg-green-600 hover:bg-green-700 rounded-full transition-colors"
                title="Add new episode"
              >
                <Plus size={20} />
              </button>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {content[language].media.episodes.map((episode, index) => (
              <div 
                key={index} 
                className={`bg-gray-900 bg-opacity-30 rounded-lg p-6 hover:bg-opacity-50 relative group transition-all duration-700 ${podcastVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                {/* Delete Button */}
                {isEditing && (
                  <button
                    onClick={() => removeFromArray(`${language}.media.episodes`, index)}
                    className="absolute top-2 right-2 z-10 bg-red-600 hover:bg-red-700 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete episode"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <EditableText
                      value={episode.title}
                      onChange={(newValue) => {
                        const updatedEpisodes = [...content[language].media.episodes];
                        updatedEpisodes[index] = { ...updatedEpisodes[index], title: newValue };
                        updateContent(`${language}.media.episodes`, updatedEpisodes);
                      }}
                      className="text-lg font-medium mb-1 block w-full"
                      placeholder="Episode title..."
                    />
                    
                    <EditableText
                      value={episode.date}
                      onChange={(newValue) => {
                        const updatedEpisodes = [...content[language].media.episodes];
                        updatedEpisodes[index] = { ...updatedEpisodes[index], date: newValue };
                        updateContent(`${language}.media.episodes`, updatedEpisodes);
                      }}
                      className="text-gray-400 text-sm block w-full"
                      placeholder="Episode date/number..."
                    />
                  </div>
                </div>
                
                <EditableText
                  value={episode.description}
                  onChange={(newValue) => {
                    const updatedEpisodes = [...content[language].media.episodes];
                    updatedEpisodes[index] = { ...updatedEpisodes[index], description: newValue };
                    updateContent(`${language}.media.episodes`, updatedEpisodes);
                  }}
                  className="text-gray-300 mb-4 leading-relaxed block w-full"
                  placeholder="Episode description..."
                  multiline
                />
                
                {isEditing ? (
                  <input
                    type="url"
                    value={episode.link}
                    onChange={(e) => {
                      const updatedEpisodes = [...content[language].media.episodes];
                      updatedEpisodes[index] = { ...updatedEpisodes[index], link: e.target.value };
                      updateContent(`${language}.media.episodes`, updatedEpisodes);
                    }}
                    className="text-white border border-gray-600 px-4 py-2 rounded bg-transparent editable-field w-full"
                    placeholder="Episode link..."
                  />
                ) : (
                  <a 
                    href={episode.link} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-white hover:text-gray-300 transition-colors border border-gray-600 px-4 py-2 rounded hover:border-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {content[language].media.listen}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Press & Features Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-center">
            {language === 'en' ? 'Press & Features' : 'Prensa y Características'}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-900 bg-opacity-30 rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-medium mb-2">
                {language === 'en' ? 'Upcoming Features' : 'Características Próximas'}
              </h4>
              <p className="text-gray-400 text-sm">
                {language === 'en'
                  ? 'Media coverage and press features coming soon'
                  : 'Cobertura mediática y características de prensa próximamente'}
              </p>
            </div>
            
            <div className="bg-gray-900 bg-opacity-30 rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-medium mb-2">
                {language === 'en' ? 'Speaking Engagements' : 'Compromisos de Oratoria'}
              </h4>
              <p className="text-gray-400 text-sm">
                {language === 'en'
                  ? 'Industry conferences and thought leadership events'
                  : 'Conferencias de la industria y eventos de liderazgo de pensamiento'}
              </p>
            </div>
            
            <div className="bg-gray-900 bg-opacity-30 rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-medium mb-2">
                {language === 'en' ? 'Video Content' : 'Contenido de Video'}
              </h4>
              <p className="text-gray-400 text-sm">
                {language === 'en'
                  ? 'Educational content and project documentaries'
                  : 'Contenido educativo y documentales de proyectos'}
              </p>
            </div>
          </div>
        </div>

        {/* Insights & Blog Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-center">
            {language === 'en' ? 'Insights & Analysis' : 'Insights y Análisis'}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 bg-opacity-30 rounded-lg p-6">
              <h4 className="text-xl font-medium mb-4">
                {language === 'en' ? 'Market Trends 2025' : 'Tendencias del Mercado 2025'}
              </h4>
              <p className="text-gray-300 mb-4">
                {language === 'en'
                  ? 'Our analysis of emerging opportunities in cross-border real estate investment and sustainable development trends shaping the Americas.'
                  : 'Nuestro análisis de oportunidades emergentes en inversión inmobiliaria transfronteriza y tendencias de desarrollo sostenible que están dando forma a las Américas.'}
              </p>
              <button className="text-white hover:text-gray-300 transition-colors inline-flex items-center">
                {language === 'en' ? 'Read More' : 'Leer Más'}
                <ExternalLink className="w-4 h-4 ml-2" />
              </button>
            </div>
            
            <div className="bg-gray-900 bg-opacity-30 rounded-lg p-6">
              <h4 className="text-xl font-medium mb-4">
                {language === 'en' ? 'AI in Construction' : 'IA en Construcción'}
              </h4>
              <p className="text-gray-300 mb-4">
                {language === 'en'
                  ? 'How artificial intelligence and emerging technologies are revolutionizing the architecture, engineering, and construction sectors.'
                  : 'Cómo la inteligencia artificial y las tecnologías emergentes están revolucionando los sectores de arquitectura, ingeniería y construcción.'}
              </p>
              <button className="text-white hover:text-gray-300 transition-colors inline-flex items-center">
                {language === 'en' ? 'Read More' : 'Leer Más'}
                <ExternalLink className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8 text-center">
          <EditableText
            value={content[language].media.newsletterTitle || (language === 'en' ? 'Stay Connected' : 'Mantente Conectado')}
            onChange={(value) => updateContent(`${language}.media.newsletterTitle`, value)}
            className="text-3xl font-bold mb-4 block w-full"
            placeholder="Newsletter title..."
          />
          
          <EditableText
            value={content[language].media.newsletterDescription || (language === 'en'
              ? 'Get insights, episode updates, and industry analysis delivered to your inbox'
              : 'Recibe insights, actualizaciones de episodios y análisis de la industria en tu bandeja de entrada')}
            onChange={(value) => updateContent(`${language}.media.newsletterDescription`, value)}
            className="text-xl text-gray-300 mb-6 block w-full"
            placeholder="Newsletter description..."
            multiline
          />
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={language === 'en' ? 'Enter your email' : 'Ingresa tu correo'}
              className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-white transition-colors text-white"
            />
            <button className="px-6 py-3 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors">
              {language === 'en' ? 'Subscribe' : 'Suscribirse'}
            </button>
          </div>
        </div>
      </div>

      {/* Social Media & Contact for Media */}
      <div className="bg-gray-900 py-16 mt-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <EditableText
            value={content[language].media.mediaInquiriesTitle || (language === 'en' ? 'Media Inquiries' : 'Consultas de Medios')}
            onChange={(value) => updateContent(`${language}.media.mediaInquiriesTitle`, value)}
            className="text-3xl font-bold mb-6 block w-full"
            placeholder="Media inquiries title..."
          />
          
          <EditableText
            value={content[language].media.mediaInquiriesDescription || (language === 'en'
              ? 'Interested in featuring Zeus or Infinity Development? We\'d love to share our story and insights.'
              : '¿Interesado en presentar a Zeus o Infinity Development? Nos encantaría compartir nuestra historia e insights.')}
            onChange={(value) => updateContent(`${language}.media.mediaInquiriesDescription`, value)}
            className="text-xl text-gray-300 mb-8 block w-full"
            placeholder="Media inquiries description..."
            multiline
          />
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors">
              {language === 'en' ? 'Media Kit' : 'Kit de Medios'}
            </button>
            <button className="px-8 py-3 border border-white text-white font-medium rounded hover:bg-white hover:text-black transition-colors">
              {language === 'en' ? 'Press Contact' : 'Contacto de Prensa'}
            </button>
          </div>
        </div>
      </div>

      {/* Editing Styles */}
      <style jsx>{`
        .editable-field {
          border: 2px dashed transparent;
          transition: all 0.2s;
        }

        .editable-field:hover {
          border-color: #3b82f6;
          background-color: rgba(59, 130, 246, 0.1);
        }

        .editable-field:focus {
          border-color: #3b82f6;
          background-color: rgba(59, 130, 246, 0.1);
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default MediaPage;